export const CARD_LAYOUTS = ["base", "borderless", "frame"];

export const CARD_LAYOUT_LABELS = {
  base: "Base",
  borderless: "Borderless",
  frame: "Frame",
};

export const FOIL_STYLES = [
  "rainbow",
  "grayscale",
  "galaxy",
  "beam",
  "diagonal_ray",
  "horizon",
  "ripple",
  "inverse_foil",
  "alternate_color",
  "3d",
  "topography",
  "rising_star",
];

export const FOIL_STYLE_LABELS = {
  rainbow: "Rainbow",
  grayscale: "Grayscale",
  galaxy: "Galaxy",
  beam: "Beam",
  diagonal_ray: "Diagonal Ray",
  horizon: "Horizon",
  ripple: "Ripple",
  inverse_foil: "Inverse Holo",
  alternate_color: "Alternate Color",
  "3d": "3D",
  topography: "Topography",
  rising_star: "Rising Star",
};

export const FOIL_STAMP_SEPARATOR = "__";

export const FOIL_STAMPS = ["draft_wizard"];

export const FOIL_STAMP_LABELS = {
  draft_wizard: "Draft Wizard",
};

export const FOIL_STAMP_ICON_FILES = {
  draft_wizard: "icon_draft_wizard.svg",
};

export const DRAFT_WIZARD_STAMP = "draft_wizard";

export function composeFoilEffect(layout, style, stamp = null) {
  const base = `${layout}_${style}`;
  if (typeof stamp === "string" && stamp) {
    return `${base}${FOIL_STAMP_SEPARATOR}${stamp}`;
  }
  return base;
}

export function parseFoilEffectId(value) {
  if (typeof value !== "string" || !value) return null;
  let stamp = null;
  let base = value;
  const stampSep = value.indexOf(FOIL_STAMP_SEPARATOR);
  if (stampSep > 0) {
    stamp = value.slice(stampSep + FOIL_STAMP_SEPARATOR.length);
    base = value.slice(0, stampSep);
    if (!FOIL_STAMPS.includes(stamp)) return null;
  }
  const sep = base.indexOf("_");
  if (sep <= 0) return null;
  const layout = base.slice(0, sep);
  const style = base.slice(sep + 1);
  if (!CARD_LAYOUTS.includes(layout) || !FOIL_STYLES.includes(style)) {
    return null;
  }
  return {
    layout,
    style,
    stamp,
    id: composeFoilEffect(layout, style, stamp),
    baseId: composeFoilEffect(layout, style),
  };
}

export function composeStampedFoilEffect(baseEffect, stamp) {
  const parsed = parseFoilEffectId(baseEffect);
  if (!parsed || !FOIL_STAMPS.includes(stamp)) return null;
  return composeFoilEffect(parsed.layout, parsed.style, stamp);
}

export const FOIL_EFFECT_POOL = CARD_LAYOUTS.flatMap((layout) =>
  FOIL_STYLES.map((style) => composeFoilEffect(layout, style)),
);

export const FOIL_STAMPED_EFFECT_POOL = FOIL_EFFECT_POOL.flatMap((base) =>
  FOIL_STAMPS.map((stamp) => `${base}${FOIL_STAMP_SEPARATOR}${stamp}`),
);

export const ALL_FOIL_EFFECT_POOL = [
  ...FOIL_EFFECT_POOL,
  ...FOIL_STAMPED_EFFECT_POOL,
];

export const DEFAULT_FOIL_EFFECT = composeFoilEffect("frame", "rainbow");

export const FOIL_EFFECT_LABELS = Object.fromEntries(
  ALL_FOIL_EFFECT_POOL.map((id) => {
    const parsed = parseFoilEffectId(id);
    if (!parsed) return [id, id];
    const baseLabel = `${CARD_LAYOUT_LABELS[parsed.layout]} ${FOIL_STYLE_LABELS[parsed.style]}`;
    if (parsed.stamp) {
      return [
        id,
        `${baseLabel} - ${FOIL_STAMP_LABELS[parsed.stamp] || parsed.stamp}`,
      ];
    }
    return [id, baseLabel];
  }),
);

const FOIL_LEGACY_MAP = {
  foil: "frame_rainbow",
  foil_beam: "frame_beam",
  reverse_holo: "frame_beam",
  galaxy_foil: "frame_galaxy",
  grayscale_foil: "frame_grayscale",
  inverted: "frame_grayscale",
  metallic_foil: "frame_rainbow",
  diagonal_foil: "frame_beam",
  shiny: "frame_diagonal_ray",
  shiny_foil: "frame_diagonal_ray",
  true: "frame_rainbow",
  rainbow: "frame_rainbow",
  grayscale: "frame_grayscale",
  galaxy: "frame_galaxy",
  beam: "frame_beam",
  diagonal_ray: "frame_diagonal_ray",
  horizon: "frame_horizon",
  ripple: "frame_ripple",
  inverse_foil: "frame_inverse_foil",
  alternate_color: "frame_alternate_color",
  "3d": "frame_3d",
  topography: "frame_topography",
  rising_star: "frame_rising_star",
  borderless: "borderless_rainbow",
  simple_frame: "frame_rainbow",
};

export function resolveFoilEffectId(foilEffect) {
  if (!foilEffect) return null;
  if (foilEffect === true) return DEFAULT_FOIL_EFFECT;
  if (typeof foilEffect !== "string") return null;
  const mapped = FOIL_LEGACY_MAP[foilEffect] || foilEffect;
  return ALL_FOIL_EFFECT_POOL.includes(mapped) ? mapped : null;
}

export function resolveFoilStyleId(foilEffect) {
  const resolved = resolveFoilEffectId(foilEffect);
  if (!resolved) return null;
  return parseFoilEffectId(resolved)?.style || null;
}

export function resolveFoilStampId(foilEffect) {
  const resolved = resolveFoilEffectId(foilEffect);
  if (!resolved) return null;
  return parseFoilEffectId(resolved)?.stamp || null;
}

export function getFoilStampIconFile(stamp) {
  if (typeof stamp !== "string" || !stamp) return null;
  return FOIL_STAMP_ICON_FILES[stamp] || null;
}

export function getCardLayout(foilEffect) {
  const resolved = resolveFoilEffectId(foilEffect);
  if (!resolved) return "base";
  return parseFoilEffectId(resolved)?.layout || "base";
}

export function foilEffectLabel(foilEffect) {
  if (!foilEffect) return "Base";
  const resolved = resolveFoilEffectId(foilEffect);
  if (!resolved) return FOIL_EFFECT_LABELS[foilEffect] || foilEffect;
  return FOIL_EFFECT_LABELS[resolved] || resolved;
}

export function getFoilArtFilterClass(foilEffect, styles) {
  if (!foilEffect || !styles) return "";
  const style = resolveFoilStyleId(foilEffect);
  if (style === "grayscale") return styles.foilArtFilterGrayscale || "";
  if (style === "inverse_foil") return styles.foilArtFilterInverse || "";
  if (style === "alternate_color") return styles.foilArtFilterAlternate || "";
  return "";
}

export function isAftermarketStyleFoil(foilEffect) {
  const style = resolveFoilStyleId(foilEffect);
  return (
    style === "rainbow" ||
    style === "grayscale" ||
    style === "galaxy" ||
    style === "beam" ||
    style === "diagonal_ray" ||
    style === "horizon" ||
    style === "ripple"
  );
}

export function isBorderlessFoil(foilEffect) {
  return getCardLayout(foilEffect) === "borderless";
}

export function isTraditionalFoil(foilEffect) {
  return getCardLayout(foilEffect) === "base";
}

export function isFrameFoil(foilEffect) {
  return getCardLayout(foilEffect) === "frame";
}

export function isLegacySpecialFoil(foilEffect) {
  const style = resolveFoilStyleId(foilEffect);
  return style === "inverse_foil" || style === "alternate_color";
}

export function compareFoilEffects(a, b) {
  const parsedA = parseFoilEffectId(resolveFoilEffectId(a) || "");
  const parsedB = parseFoilEffectId(resolveFoilEffectId(b) || "");
  if (!parsedA && !parsedB) return 0;
  if (!parsedA) return 1;
  if (!parsedB) return -1;
  const layoutDelta =
    CARD_LAYOUTS.indexOf(parsedA.layout) - CARD_LAYOUTS.indexOf(parsedB.layout);
  if (layoutDelta) return layoutDelta;
  const styleDelta =
    FOIL_STYLES.indexOf(parsedA.style) - FOIL_STYLES.indexOf(parsedB.style);
  if (styleDelta) return styleDelta;
  const stampA = parsedA.stamp ? FOIL_STAMPS.indexOf(parsedA.stamp) : -1;
  const stampB = parsedB.stamp ? FOIL_STAMPS.indexOf(parsedB.stamp) : -1;
  return stampA - stampB;
}

export function sortFoilEffects(list) {
  if (!Array.isArray(list)) return [];
  return [...list].sort(compareFoilEffects);
}

export function hashFoilSeed(str) {
  let hash = 2166136261;
  const s = String(str || "");
  for (let i = 0; i < s.length; i += 1) {
    hash ^= s.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

export function createFoilRng(seed) {
  let state = seed >>> 0;
  return () => {
    state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
    return state / 4294967296;
  };
}

const galaxyPatternCache = new Map();

export function galaxyPatternVars(cardId) {
  const key = String(cardId || "galaxy");
  const cached = galaxyPatternCache.get(key);
  if (cached) return cached;
  const rand = createFoilRng(hashFoilSeed(key));
  const hue = Math.floor(rand() * 360);
  const vars = {
    "--cosmos-bg-x": `${(rand() * 100).toFixed(1)}%`,
    "--cosmos-bg-y": `${(rand() * 100).toFixed(1)}%`,
    "--cosmos-bg-x2": `${(rand() * 100).toFixed(1)}%`,
    "--cosmos-bg-y2": `${(rand() * 100).toFixed(1)}%`,
    "--galaxy-hue": `${hue}deg`,
    "--galaxy-hue-alt": `${(hue + 40 + Math.floor(rand() * 80)) % 360}deg`,
    "--galaxy-angle": `${(72 + rand() * 36).toFixed(1)}deg`,
    "--galaxy-space": `${(3.2 + rand() * 2.2).toFixed(2)}%`,
    "--galaxy-size": `${(110 + rand() * 50).toFixed(1)}%`,
  };
  galaxyPatternCache.set(key, vars);
  return vars;
}

export function staticFxPose(seed) {
  const rand = createFoilRng((hashFoilSeed(seed) >>> 0) ^ 0x51f00d);
  const mx = 0.28 + rand() * 0.44;
  const my = 0.26 + rand() * 0.48;
  return {
    mx,
    my,
    bx: 20 + mx * 60,
    by: 18 + my * 64,
    beamAngle: 95 + (mx - 0.5) * 55 + (my - 0.5) * 20,
    fromCenter: Math.min(
      1,
      Math.sqrt((mx - 0.5) ** 2 + (my - 0.5) ** 2) / 0.5,
    ),
    fromLeft: mx,
    fromTop: my,
  };
}

export function poseStyleFromVars(pose, opacities = null) {
  if (!pose && !opacities) return undefined;
  const style = {};
  if (pose) {
    style["--mx"] = String(pose.mx);
    style["--my"] = String(pose.my);
    style["--bx"] = String(pose.bx);
    style["--by"] = String(pose.by);
    style["--beam-angle"] = `${pose.beamAngle}deg`;
    style["--pointer-from-center"] = String(pose.fromCenter);
    style["--pointer-from-left"] = String(pose.fromLeft ?? pose.mx);
    style["--pointer-from-top"] = String(pose.fromTop ?? pose.my);
  }
  if (opacities) {
    style["--shine-opacity"] = String(opacities.shine);
    style["--foil-opacity"] = String(opacities.foil);
    style["--glare-opacity"] = String(opacities.glare);
  }
  return style;
}

export function fxOpacities(foilType, quality = "maximum") {
  const min = quality === "minimum";
  const style = FOIL_STYLES.includes(foilType)
    ? foilType
    : resolveFoilStyleId(foilType);
  if (!style) {
    return {
      shine: min ? 0.28 : 0.4,
      foil: 0,
      glare: min ? 0.62 : 0.88,
    };
  }
  if (style === "galaxy") {
    return { shine: 0.9, foil: min ? 0.7 : 0.9, glare: 0.72 };
  }
  if (style === "beam" || style === "diagonal_ray" || style === "horizon") {
    return { shine: 0.9, foil: min ? 0.7 : 0.9, glare: 0.75 };
  }
  if (style === "ripple") {
    return { shine: 0, foil: 0, glare: min ? 0.62 : 0.86 };
  }
  if (style === "3d") {
    return {
      shine: min ? 0.28 : 0.38,
      foil: min ? 0.28 : 0.38,
      glare: min ? 0.55 : 0.78,
    };
  }
  if (style === "rising_star") {
    return {
      shine: min ? 0.45 : 0.6,
      foil: min ? 0.75 : 0.95,
      glare: min ? 0.45 : 0.6,
    };
  }
  if (style === "topography") {
    return {
      shine: min ? 0.55 : 0.75,
      foil: min ? 0.72 : 0.92,
      glare: min ? 0.45 : 0.62,
    };
  }
  if (
    style === "rainbow" ||
    style === "grayscale" ||
    style === "inverse_foil" ||
    style === "alternate_color"
  ) {
    return { shine: 0.9, foil: min ? 0.7 : 0.9, glare: min ? 0.6 : 0.8 };
  }
  return { shine: 0.75, foil: min ? 0.55 : 0.78, glare: min ? 0.45 : 0.6 };
}

export const LIVE_FX = {
  cardOpacity: "0.9",
  shineOpacity: "0.75",
  foilOpacity: "0.85",
  glareOpacity: "0.75",
};

export function liveFxFromPointer(mx, my) {
  const x = Math.min(0.82, Math.max(0.18, mx));
  const y = Math.min(0.82, Math.max(0.18, my));
  const fromCenter = Math.min(
    1,
    Math.sqrt((x - 0.5) ** 2 + (y - 0.5) ** 2) / 0.5,
  );
  return {
    mx: x,
    my: y,
    bx: 20 + x * 60,
    by: 18 + y * 64,
    beamAngle: 95 + (x - 0.5) * 70,
    fromCenter,
    fromLeft: x,
    fromTop: y,
  };
}

export function applyLiveFxVars(el, { mx, my, hue, includeOpacities = true } = {}) {
  if (!el) return;
  const pose = liveFxFromPointer(mx ?? 0.5, my ?? 0.5);
  el.style.setProperty("--mx", String(pose.mx));
  el.style.setProperty("--my", String(pose.my));
  el.style.setProperty("--bx", String(pose.bx));
  el.style.setProperty("--by", String(pose.by));
  el.style.setProperty("--beam-angle", `${pose.beamAngle}deg`);
  el.style.setProperty("--pointer-from-center", String(pose.fromCenter));
  el.style.setProperty("--pointer-from-left", String(pose.fromLeft));
  el.style.setProperty("--pointer-from-top", String(pose.fromTop));
  if (hue != null) {
    el.style.setProperty("--foil-hue", String(hue));
  }
  if (includeOpacities) {
    el.style.setProperty("--shine-opacity", LIVE_FX.shineOpacity);
    el.style.setProperty("--foil-opacity", LIVE_FX.foilOpacity);
    el.style.setProperty("--glare-opacity", LIVE_FX.glareOpacity);
    el.style.setProperty("--card-opacity", LIVE_FX.cardOpacity);
  }
}

export function shineTiltProfile(foilEffect) {
  const hasFoil = Boolean(resolveFoilStyleId(foilEffect));
  return {
    foil: hasFoil,
    premium: hasFoil,
    tiltDivisor: hasFoil ? 1.55 : 1.75,
    interactScale: hasFoil ? 1.08 : 1.06,
  };
}
